/**
 * MessageList - Container for rendering chat messages
 */

import { useRef, useEffect, useState, useCallback, useMemo } from 'react'
import clsx from 'clsx'
import { ArrowDown } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { UserMessage } from './UserMessage'
// PR 12 (gap closure): mount the response-formatting router so the
// formatting_v2 PostHog flag drives the renderer choice. The legacy
// AssistantMessage is still served when the flag is off OR when the
// message has no v2-typed parts (legacy v1 row, in-flight stream).
import { AssistantMessageRouter as AssistantMessage } from './AssistantMessageRouter'
import { StreamingIndicator } from './StreamingIndicator'
import { OrchestrationStatus, type OrchestrationStatusData } from './OrchestrationStatus'
import { ToolProgressStatus, type ToolProgressData } from './ToolProgressStatus'
import { MessageSkeleton } from './MessageSkeleton'
import { ImagePreviewModal } from './ImagePreviewModal'
import type { UIMessage, AgentId, VersionInfo } from '../../types'
import { AUTO_SCROLL_THRESHOLD } from '../../constants'

interface MessageListProps {
  messages: UIMessage[]
  conversationId?: string | null
  isLoading?: boolean
  isLoadingMessages?: boolean
  activeAgent: AgentId
  onFeedback?: (messageId: string, rating: number, feedbackMessage?: string, screenshot?: string, userQuestion?: string, assistantAnswer?: string) => void
  onRegenerate?: () => void
  onSendFollowUp?: (question: string) => void
  onEditMessage?: (messageId: string, newContent: string, onEditSaved?: () => void) => void
  orchestrationStatus?: OrchestrationStatusData | null
  toolProgress?: ToolProgressData | null
  getVersionInfo?: (message: UIMessage) => VersionInfo | null
  onNavigateVersion?: (userMsgId: string, direction: 'prev' | 'next') => void
  /** FS-676: answer a message's egress-consent prompt (approve/deny card) */
  onConsentResponse?: (messageId: string, verdict: 'granted' | 'denied') => void
}

export function MessageList({
  messages,
  conversationId,
  isLoading = false,
  isLoadingMessages = false,
  activeAgent,
  onFeedback,
  onRegenerate,
  onSendFollowUp,
  onEditMessage,
  orchestrationStatus,
  toolProgress,
  getVersionInfo,
  onNavigateVersion,
  onConsentResponse,
}: MessageListProps) {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const lastUserMessageRef = useRef<HTMLDivElement>(null)
  const shouldAutoScroll = useRef(true)
  const [previewImage, setPreviewImage] = useState<string | null>(null)
  const [showScrollButton, setShowScrollButton] = useState(false)
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null)
  const [isEditSaving, setIsEditSaving] = useState(false)

  const handleImageClick = useCallback((imageUrl: string) => {
    setPreviewImage(imageUrl)
  }, [])

  // Handle edit message
  const handleEditConfirm = useCallback(async (messageId: string, newContent: string) => {
    if (!onEditMessage) return

    setIsEditSaving(true)
    try {
      // Pass callback to stop loading after edit is saved (before streaming)
      await onEditMessage(messageId, newContent, () => {
        setIsEditSaving(false)
        setEditingMessageId(null)
      })
    } catch {
      // On error, also stop loading
      setIsEditSaving(false)
    }
  }, [onEditMessage])

  const handleEditCancel = useCallback(() => {
    setEditingMessageId(null)
  }, [])

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    const container = containerRef.current
    if (!container) return
    container.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth',
    })
  }, [])

  // Track previous message count to detect new messages
  const prevMessageCountRef = useRef(messages.length)

  // Handle auto-scroll - scroll new messages to the top of the chat window
  useEffect(() => {
    const prevCount = prevMessageCountRef.current
    const currentCount = messages.length
    prevMessageCountRef.current = currentCount

    // If a new message was added, scroll user's message to the top
    if (currentCount > prevCount && currentCount > 0) {
      // Use setTimeout to ensure DOM is fully updated before scrolling
      setTimeout(() => {
        lastUserMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 50)
    }
  }, [messages])

  // Track scroll position to determine if we should auto-scroll and show button
  const handleScroll = () => {
    const container = containerRef.current
    if (!container) return

    const { scrollTop, scrollHeight, clientHeight } = container
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight
    shouldAutoScroll.current = distanceFromBottom < AUTO_SCROLL_THRESHOLD
    setShowScrollButton(distanceFromBottom > 200)
  }

  // Filter out system messages — they are internal and should never render.
  // Computed BEFORE the isLoadingMessages early return so React-hooks
  // rules are respected — useMemo must be called unconditionally on
  // every render. Pre-existing rules-of-hooks lint error surfaced by
  // CI when this file changed for W16; fixed here per CLAUDE.md
  // "tests/lint stay green when the file is touched" rule.
  const visibleMessages = useMemo(() => messages.filter((m) => m.role !== 'system'), [messages])

  // Build a lookup: message ID → preceding user question text (O(N) single pass)
  const userQuestionByMessageId = useMemo(() => {
    const map: Record<string, string | undefined> = {}
    let lastUserQuestion: string | undefined
    for (const msg of visibleMessages) {
      if (msg.role === 'user') {
        const textPart = msg.parts?.find(p => p.type === 'text')
        lastUserQuestion = textPart ? (textPart as { type: 'text'; text: string }).text : undefined
      } else {
        map[msg.id] = lastUserQuestion
      }
    }
    return map
  }, [visibleMessages])

  // Show skeleton while loading conversation messages
  if (isLoadingMessages) {
    return (
      <div className="h-full overflow-y-auto px-4 py-6 bg-gradient-to-br from-orange-100/60 via-amber-50/40 to-white dark:from-dark-bg dark:via-dark-bg dark:to-dark-bg">
        <div className="w-full">
          <MessageSkeleton count={4} />
        </div>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      data-messages-container
      className="relative h-full overflow-y-auto px-4 py-6 bg-gradient-to-br from-orange-100/60 via-amber-50/40 to-white dark:from-dark-bg dark:via-dark-bg dark:to-dark-bg"
    >
      <div className="w-full space-y-6 pb-[50vh]">
        {(() => {
          // Find the index of the last user message for scrolling
          const lastUserMessageIndex = visibleMessages.findLastIndex(m => m.role === 'user')

          return visibleMessages.map((message, index) => {
            const isLastMessage = index === visibleMessages.length - 1
            const isLastUserMessage = index === lastUserMessageIndex
            const isStreaming = isLoading && isLastMessage && message.role === 'assistant'
            const isLastAssistantMessage = isLastMessage && message.role === 'assistant'

            // Calculate messages after this one (for edit warning)
            const messagesAfterCount = visibleMessages.length - 1 - index

            // Can edit if not loading, there's an edit handler, and no other message is being edited
            const canEdit = !isLoading && !!onEditMessage && (!editingMessageId || editingMessageId === message.id)

            return (
              <div key={message.id} ref={isLastUserMessage ? lastUserMessageRef : null} data-message-id={message.id}>
              {message.role === 'user' ? (
                <UserMessage
                  message={message}
                  onImageClick={handleImageClick}
                  isEditing={editingMessageId === message.id}
                  isEditSaving={isEditSaving}
                  messagesAfterCount={messagesAfterCount}
                  onEditClick={canEdit ? () => setEditingMessageId(message.id) : undefined}
                  onEditConfirm={(newContent) => handleEditConfirm(message.id, newContent)}
                  onEditCancel={handleEditCancel}
                  canEdit={canEdit}
                />
              ) : (
                <AssistantMessage
                  message={message}
                  conversationId={conversationId || undefined}
                  isStreaming={isStreaming}
                  isLastMessage={isLastAssistantMessage}
                  userQuestion={userQuestionByMessageId[message.id]}
                  onFeedback={
                    onFeedback
                      ? (rating, feedbackMessage, screenshot, userQuestion, assistantAnswer) => onFeedback(message.id, rating, feedbackMessage, screenshot, userQuestion, assistantAnswer)
                      : undefined
                  }
                  onRegenerate={isLastAssistantMessage ? onRegenerate : undefined}
                  onSendFollowUp={onSendFollowUp}
                  onImageClick={handleImageClick}
                  versionInfo={getVersionInfo?.(message) ?? null}
                  onNavigateVersion={onNavigateVersion}
                  onConsentResponse={
                    onConsentResponse
                      ? (verdict) => onConsentResponse(message.id, verdict)
                      : undefined
                  }
                />
              )}
            </div>
            )
          })
        })()}

        {/* Orchestration status - show when loading and have orchestration data */}
        {isLoading && orchestrationStatus && (
          <div className="flex justify-start">
            <OrchestrationStatus
              agentId={activeAgent}
              status={orchestrationStatus.status}
              description={orchestrationStatus.description}
              documents={orchestrationStatus.documents}
            />
          </div>
        )}

        {/* W16 / TIME-198: ephemeral tool-progress status line. Renders
         *  alongside (not instead of) OrchestrationStatus — a slow tool
         *  call is a layer on top of the orchestrator heartbeat.
         *  Mounts when a tool emits invoked/binding_resolved/fetching,
         *  unmounts on completed/failed (handled in useChatV2). */}
        {isLoading && toolProgress && (
          <div className="flex justify-start">
            <ToolProgressStatus data={toolProgress} />
          </div>
        )}

        {/* Streaming indicator - show when waiting for first response (no orchestration yet) */}
        {isLoading && !orchestrationStatus && messages.length > 0 && messages[messages.length - 1].role === 'user' && (
          <div className="flex justify-start">
            <StreamingIndicator />
          </div>
        )}
      </div>

      {/* Scroll to bottom button */}
      {showScrollButton && (
        <button
          onClick={scrollToBottom}
          className={clsx(
            'absolute bottom-4 right-4 p-3 rounded-full shadow-lg',
            'bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border',
            'hover:bg-gray-50 dark:hover:bg-dark-muted transition-all',
            'animate-in fade-in slide-in-from-bottom-2 duration-200'
          )}
          aria-label={t('chatv2:messages.scroll_to_bottom', { defaultValue: 'Scroll to bottom' })}
        >
          <ArrowDown className="h-5 w-5 text-gray-600 dark:text-gray-400" />
        </button>
      )}

      {/* Image preview modal */}
      {previewImage && (
        <ImagePreviewModal
          imageUrl={previewImage}
          onClose={() => setPreviewImage(null)}
        />
      )}
    </div>
  )
}

export default MessageList
